import {
    CardSlider,
    CardSliderItem,
    CardSliderItemRow,
} from "@hatch-digital-nl/bravilor-storybook";
import { JSX } from "react";
import { useNavigate } from "react-router-dom";
import { ProductIndexItemInterface } from "../../interfaces/apiV2/shared/ProductIndexItemInterface";
import {
    ProductAddOnCardInterface,
    ProductAssociationCardInterface,
} from "../../interfaces/apiV2/product/ProductAssociatedInterface";
import { ProductTypeEnum } from "../../enums/ProductTypeEnum";
import { RouteEnum } from "../../enums/RouteEnum";
import { useStringTranslation } from "../../hooks/useStringTranslation";

type RelatedProductCard =
    | ProductIndexItemInterface
    | ProductAssociationCardInterface
    | ProductAddOnCardInterface;

interface Props {
    products?: RelatedProductCard[];
    title: string;
    type?: ProductTypeEnum;
    showPrice?: boolean;
    loading?: boolean;
    fullWidth?: boolean;
}

const buildCardRows = (
    product: RelatedProductCard,
    includePrice: boolean,
    artNumberText: string,
): CardSliderItemRow[] => {
    const rows: CardSliderItemRow[] = [
        { label: artNumberText, value: product.identifier },
    ];

    if (includePrice && "prices" in product && product.prices?.value) {
        rows.push({
            label: product.prices.label ?? "",
            value: product.prices.value,
        });
    }

    const similarSpec =
        "similarProductsSpecification" in product
            ? product.similarProductsSpecification
            : undefined;

    if (similarSpec) {
        rows.push({
            label: similarSpec.label,
            value: similarSpec.value,
        });
    }

    return rows;
};

export const RelatedProducts = (props: Props): JSX.Element | null => {
    const navigate = useNavigate();
    const { t, translations } = useStringTranslation();

    if (!props.loading && (!props.products || props.products.length === 0)) {
        return null;
    }

    const includePrice =
        props.showPrice === true || props.type === ProductTypeEnum.accessories;

    const artNumberText = translations ? t("art-nr") : "Art. Nr.";

    const viewProductText = translations ? t("view-product") : "View Product";

    const totalLabel = translations ? t("total") : "total";

    return (
        <CardSlider
            title={props.title}
            totalCount={props.loading ? undefined : props.products?.length}
            totalLabel={props.loading ? undefined : totalLabel}
            loading={props.loading}
            slidesPerView={props.fullWidth ? 6 : undefined}
            loadingItemsCount={props.fullWidth ? 6 : undefined}
            className={
                props.fullWidth
                    ? "c-card-slider--full-width"
                    : "c-card-slider--product-detail"
            }
        >
            {(props.products ?? []).map((product) => (
                <CardSliderItem
                    key={product.id}
                    title={product.label ?? ""}
                    image={product.image ?? ""}
                    rows={buildCardRows(product, includePrice, artNumberText)}
                    buttonText={viewProductText}
                    onButtonClick={() =>
                        navigate(`${RouteEnum.PRODUCT_RANGE}/${product.id}`)
                    }
                />
            ))}
        </CardSlider>
    );
};
